import React from "react";
import { Grid } from "@mui/material";
import SummaryCard, { SummaryCardIcon } from "./SummaryCard";
import { DashboardData } from "../../shared/types/dashboard";
import { useUser } from "src/shared/context/UserContext";
import { useFormatAmount } from "src/business-types/laundry/modules/home/hooks/useFormatAmount";

interface SummaryCardsProps {
  dashboardData: DashboardData;
  loading?: boolean;
}

interface CardConfig {
  title: string;
  value: string | number;
  icon: SummaryCardIcon;
  previousValue?: number;
}

const SummaryCards: React.FC<SummaryCardsProps> = ({ dashboardData, loading = false }) => {
  const { userConfigration } = useUser();
  const toBoolean = (value: any) => value === true || value === "true";
  const deliveryFeeEnabled = toBoolean(userConfigration?.deliveryFeeEnabled);
  const FormatAmount = useFormatAmount();

  const baseCards: CardConfig[] = [
    {
      title: "Total Sales",
      value: FormatAmount(dashboardData.totalSales),
      icon: "localAtm",
    },
    {
      title: "Total Orders",
      value: dashboardData.totalOrders.toLocaleString("en-US"),
      icon: "receipt",
    },
    {
      title: "Received Amount",
      value: FormatAmount(dashboardData.receivedAmount || 0),
      icon: "payment",
    },
    {
      title: "Tip Amount",
      value: FormatAmount(dashboardData.totalTip || 0),
      icon: "restaurant",
    },
    ...(deliveryFeeEnabled
      ? [
          {
            title: "Delivery Fee",
            value: FormatAmount(dashboardData.totalDeliveryFee || 0),
            icon: "localShipping" as SummaryCardIcon,
          },
        ]
      : []),
  ];

  if (loading) {
    return (
      <Grid container spacing={3} className="mb-0.5">
        {Array.from({ length: deliveryFeeEnabled ? 5 : 4 }).map((_, index) => (
          <Grid item xs={12} sm={6} md={deliveryFeeEnabled ? 2.4 : 3} key={index}>
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 animate-pulse">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                <div className="w-16 h-6 bg-gray-200 rounded-full"></div>
              </div>
              <div className="space-y-3">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-8 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          </Grid>
        ))}
      </Grid>
    );
  }

  // Calculate responsive breakpoints based on number of cards
  const cardCount = baseCards.length;

  return (
    <Grid container spacing={3} className="mb-8">
      {baseCards.map((card, index) => {
        return (
          <Grid
            item
            xs={12}
            sm={6}
            md={cardCount === 5 ? 2.4 : 3}
            lg={cardCount === 5 ? 2.4 : 3}
            key={index}
            sx={{
              // Ensure consistent height
              display: "flex",
              "& > *": {
                width: "100%",
                height: "100%",
              },
            }}
          >
            <SummaryCard {...card} />
          </Grid>
        );
      })}
    </Grid>
  );
};

export default SummaryCards;
